import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import FloatingActionButton from '../../components/ui/FloatingActionButton';
import { useToast, ToastContainer } from '../../components/ui/Toast';

// Import components
import GuestCard from './components/GuestCard';
import FilterToolbar from './components/FilterToolbar';
import AddGuestModal from './components/AddGuestModal';
import ImportContactsModal from './components/ImportContactsModal';
import GuestHistoryModal from './components/GuestHistoryModal';
import FamilyGroupCard from './components/FamilyGroupCard';

const GuestManagement = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError } = useToast();

  // State management
  const [activeView, setActiveView] = useState('guests'); // guests, families
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    dietaryTypes: [],
    familyGroups: [],
    activity: []
  });

  // Modal states
  const [isAddGuestModalOpen, setIsAddGuestModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [editingGuest, setEditingGuest] = useState(null);

  // Mock current user
  const currentUser = {
    name: "Alex Thompson",
    email: "alex@example.com"
  };

  // Mock guests data
  const [guests, setGuests] = useState([
  {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    phone: "+1 (555) 123-4567",
    avatar: "https://images.unsplash.com/photo-1637562772116-e01cda44fce8",
    avatarAlt: "Professional headshot of woman with shoulder-length brown hair in navy blazer",
    familyGroup: "Johnson Family",
    dietaryRestrictions: [
    { name: "Peanuts", type: "allergy", severity: "severe" },
    { name: "Vegetarian", type: "preference" }],

    totalVisits: 8,
    lastVisit: "2024-10-15",
    recentVisits: [
    { eventName: "Holiday Dinner", date: "2024-10-15", dishesServed: 3 },
    { eventName: "Summer BBQ", date: "2024-08-22", dishesServed: 4 }],

    notes: "Loves Mediterranean cuisine. Always brings wine."
  },
  {
    id: 2,
    name: "Michael Chen",
    email: "m.chen@company.com",
    phone: "+1 (555) 234-5678",
    avatar: "https://images.unsplash.com/photo-1629272039203-7d76fdaf1324",
    avatarAlt: "Professional headshot of Asian man with short black hair in dark suit",
    familyGroup: null,
    dietaryRestrictions: [
    { name: "Lactose", type: "intolerance", severity: "moderate" },
    { name: "Spicy Food", type: "preference" }],

    totalVisits: 12,
    lastVisit: "2024-10-10",
    recentVisits: [
    { eventName: "Birthday Celebration", date: "2024-10-10", dishesServed: 5 },
    { eventName: "Game Night Dinner", date: "2024-09-28", dishesServed: 3 }],

    notes: "Great conversationalist. Prefers Asian fusion dishes."
  },
  {
    id: 3,
    name: "Emily Rodriguez",
    email: "emily.r@email.com",
    phone: "+1 (555) 345-6789",
    avatar: "https://images.unsplash.com/photo-1719515862094-c6e9354ee7f8",
    avatarAlt: "Professional headshot of Hispanic woman with long dark hair in white blouse",
    familyGroup: "Rodriguez Family",
    dietaryRestrictions: [
    { name: "Gluten", type: "intolerance", severity: "mild" },
    { name: "Shellfish", type: "allergy", severity: "severe" }],

    totalVisits: 6,
    lastVisit: "2024-09-30",
    recentVisits: [
    { eventName: "Autumn Feast", date: "2024-09-30", dishesServed: 4 }],

    notes: "Excellent baker. Often helps with desserts."
  },
  {
    id: 4,
    name: "David Kim",
    email: "david.kim@work.com",
    phone: "+1 (555) 456-7890",
    avatar: "https://images.unsplash.com/photo-1698072556534-40ec6e337311",
    avatarAlt: "Professional headshot of Korean man with glasses and friendly smile in blue shirt",
    familyGroup: null,
    dietaryRestrictions: [],
    totalVisits: 3,
    lastVisit: "2024-09-15",
    recentVisits: [
    { eventName: "Welcome Dinner", date: "2024-09-15", dishesServed: 3 }],

    notes: "New to the area. Very adventurous eater."
  },
  {
    id: 5,
    name: "Lisa Thompson",
    email: "lisa@email.com",
    phone: "+1 (555) 567-8901",
    avatar: "https://images.unsplash.com/photo-1663834221517-cf78904dea4e",
    avatarAlt: "Professional headshot of blonde woman with warm smile in cream colored sweater",
    familyGroup: "Thompson Family",
    dietaryRestrictions: [
    { name: "Vegan", type: "preference" },
    { name: "Nuts", type: "allergy", severity: "moderate" }],

    totalVisits: 15,
    lastVisit: "2024-10-20",
    recentVisits: [
    { eventName: "Harvest Dinner", date: "2024-10-20", dishesServed: 6 },
    { eventName: "Wine Tasting", date: "2024-10-05", dishesServed: 4 }],

    notes: "Frequent guest. Loves trying new vegan recipes."
  }]
  );

  // Mock family groups data
  const [familyGroups, setFamilyGroups] = useState([
  {
    id: 1,
    name: "Johnson Family",
    members: [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@email.com",
      avatar: "https://images.unsplash.com/photo-1637562772116-e01cda44fce8",
      avatarAlt: "Professional headshot of woman with shoulder-length brown hair in navy blazer",
      dietaryRestrictions: [
      { name: "Peanuts", type: "allergy", severity: "severe" },
      { name: "Vegetarian", type: "preference" }]

    }],

    totalVisits: 8,
    lastVisit: "2024-10-15",
    notes: "Close family friends. Always RSVP early."
  },
  {
    id: 2,
    name: "Rodriguez Family",
    members: [
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.r@email.com",
      avatar: "https://images.unsplash.com/photo-1719515862094-c6e9354ee7f8",
      avatarAlt: "Professional headshot of Hispanic woman with long dark hair in white blouse",
      dietaryRestrictions: [
      { name: "Gluten", type: "intolerance", severity: "mild" },
      { name: "Shellfish", type: "allergy", severity: "severe" }]

    }],

    totalVisits: 6,
    lastVisit: "2024-09-30",
    notes: "Love hosting potluck style dinners."
  },
  {
    id: 3,
    name: "Thompson Family",
    members: [
    {
      id: 5,
      name: "Lisa Thompson",
      email: "lisa@email.com",
      avatar: "https://images.unsplash.com/photo-1663834221517-cf78904dea4e",
      avatarAlt: "Professional headshot of blonde woman with warm smile in cream colored sweater",
      dietaryRestrictions: [
      { name: "Vegan", type: "preference" },
      { name: "Nuts", type: "allergy", severity: "moderate" }]

    }],

    totalVisits: 15,
    lastVisit: "2024-10-20",
    notes: "Very health-conscious family. Prefer organic ingredients."
  }]
  );

  // Filter guests based on search and filters
  const filteredGuests = useMemo(() => {
    return guests?.filter((guest) => {
      // Search filter
      const matchesSearch = guest?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      guest?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      // Dietary type filter
      const matchesDietaryTypes = filters?.dietaryTypes?.length === 0 ||
      filters?.dietaryTypes?.some((type) =>
      guest?.dietaryRestrictions?.some((restriction) => restriction?.type === type)
      );

      // Family group filter
      const matchesFamilyGroups = filters?.familyGroups?.length === 0 ||
      filters?.familyGroups?.includes('has_family') && guest?.familyGroup ||
      filters?.familyGroups?.includes('no_family') && !guest?.familyGroup;

      // Activity filter
      const matchesActivity = filters?.activity?.length === 0 ||
      filters?.activity?.some((activity) => {
        const now = new Date();
        const lastVisit = guest?.lastVisit ? new Date(guest.lastVisit) : null;

        switch (activity) {
          case 'recent':
            return lastVisit && now - lastVisit <= 30 * 24 * 60 * 60 * 1000; // 30 days
          case 'frequent':
            return guest?.totalVisits >= 5;
          case 'new':
            return guest?.totalVisits <= 2;
          default:
            return true;
        }
      });

      return matchesSearch && matchesDietaryTypes && matchesFamilyGroups && matchesActivity;
    });
  }, [guests, searchTerm, filters]);

  // Event handlers
  const handleLogout = () => {
    navigate('/login');
  };

  const handleFloatingAction = (actionType) => {
    switch (actionType) {
      case 'add-guest':
        setIsAddGuestModalOpen(true);
        break;
      case 'import-guests':
        setIsImportModalOpen(true);
        break;
      case 'new-event':navigate('/dinner-planning');
        break;
      case 'add-recipe':navigate('/dish-library');
        break;
      default:
        break;
    }
  };

  const handleSaveGuest = (guestData) => {
    if (editingGuest) {
      // Update existing guest
      setGuests((prev) => prev?.map((guest) =>
      guest?.id === editingGuest?.id ?
      { ...guest, ...guestData } :
      guest
      ));
      showSuccess('Guest updated successfully');
      setEditingGuest(null);
    } else {
      // Add new guest
      const newGuest = {
        ...guestData,
        id: Date.now(),
        avatar: "https://images.unsplash.com/photo-1587050063131-1ac5bf6d7e7e",
        avatarAlt: "Professional headshot placeholder with neutral background",
        totalVisits: 0,
        lastVisit: null,
        recentVisits: []
      };
      setGuests((prev) => [...prev, newGuest]);
      showSuccess('Guest added successfully');
    }
    setIsAddGuestModalOpen(false);
  };

  const handleEditGuest = (guest) => {
    setEditingGuest(guest);
    setIsAddGuestModalOpen(true);
  };

  const handleDeleteGuest = (guest) => {
    if (window.confirm(`Are you sure you want to delete ${guest?.name}?`)) {
      setGuests((prev) => prev?.filter((g) => g?.id !== guest?.id));
      showSuccess('Guest deleted successfully');
    }
  };

  const handleViewHistory = (guest) => {
    setSelectedGuest(guest);
    setIsHistoryModalOpen(true);
  };

  const handleImportContacts = (contacts, duplicateHandling) => {
    const newGuests = contacts?.map((contact) => ({
      ...contact,
      id: Date.now() + Math.random(),
      avatar: "https://images.unsplash.com/photo-1587050063131-1ac5bf6d7e7e",
      avatarAlt: "Professional headshot placeholder with neutral background",
      dietaryRestrictions: [],
      totalVisits: 0,
      lastVisit: null,
      recentVisits: [],
      notes: ''
    }));

    setGuests((prev) => [...prev, ...newGuests]);
    showSuccess(`Successfully imported ${newGuests?.length} contacts`);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      dietaryTypes: [],
      familyGroups: [],
      activity: []
    });
  };

  const handleEditFamilyGroup = (familyGroup) => {
    // Placeholder for family group editing
    showSuccess('Family group editing feature coming soon');
  };

  const handleDeleteFamilyGroup = (familyGroup) => {
    if (window.confirm(`Are you sure you want to delete ${familyGroup?.name}?`)) {
      setFamilyGroups((prev) => prev?.filter((fg) => fg?.id !== familyGroup?.id));
      showSuccess('Family group deleted successfully');
    }
  };

  const handleViewFamilyMembers = (familyGroup) => {
    // Placeholder for viewing family members
    showSuccess('Family member management feature coming soon');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} onLogout={handleLogout} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Guest Management</h1>
              <p className="text-muted-foreground">
                Organize your contacts, dietary profiles, and family groupings for efficient dinner planning.
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                onClick={() => setIsImportModalOpen(true)}
                iconName="Upload"
                iconPosition="left"
                className="hidden lg:flex">

                Import Contacts
              </Button>
              <Button
                variant="default"
                onClick={() => setIsAddGuestModalOpen(true)}
                iconName="UserPlus"
                iconPosition="left"
                className="hidden lg:flex">

                Add Guest
              </Button>
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center space-x-1 mb-6 bg-muted p-1 rounded-lg w-fit">
            <button
              onClick={() => setActiveView('guests')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeView === 'guests' ? 'bg-card text-foreground shadow-warm' : 'text-muted-foreground hover:text-foreground'}`
              }>

              <Icon name="User" size={16} className="inline mr-2" />
              Individual Guests ({filteredGuests?.length})
            </button>
            <button
              onClick={() => setActiveView('families')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeView === 'families' ? 'bg-card text-foreground shadow-warm' : 'text-muted-foreground hover:text-foreground'}`
              }>

              <Icon name="Users" size={16} className="inline mr-2" />
              Family Groups ({familyGroups?.length})
            </button>
          </div>

          {/* Filter Toolbar */}
          <FilterToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filters={filters}
            onFilterChange={setFilters}
            totalGuests={activeView === 'guests' ? guests?.length : familyGroups?.length}
            filteredCount={activeView === 'guests' ? filteredGuests?.length : familyGroups?.length}
            onClearFilters={handleClearFilters} />


          {/* Content Area */}
          {activeView === 'guests' ?
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredGuests?.map((guest) =>
            <GuestCard
              key={guest?.id}
              guest={guest}
              onEdit={handleEditGuest}
              onDelete={handleDeleteGuest}
              onViewHistory={handleViewHistory} />

            )}
              
              {filteredGuests?.length === 0 &&
            <div className="col-span-full text-center py-12">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    {searchTerm || Object.values(filters)?.some((f) => f?.length > 0) ?
                'No guests match your filters' : 'No guests yet'
                }
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {searchTerm || Object.values(filters)?.some((f) => f?.length > 0) ?
                'Try adjusting your search or filter criteria.' : 'Start by adding your first guest or importing contacts.'
                }
                  </p>
                  {!searchTerm && !Object.values(filters)?.some((f) => f?.length > 0) &&
              <div className="flex items-center justify-center space-x-3">
                      <Button
                  variant="default"
                  onClick={() => setIsAddGuestModalOpen(true)}
                  iconName="UserPlus"
                  iconPosition="left">

                        Add First Guest
                      </Button>
                      <Button
                  variant="outline"
                  onClick={() => setIsImportModalOpen(true)}
                  iconName="Upload"
                  iconPosition="left">

                        Import Contacts
                      </Button>
                    </div>
              }
                </div>
            }
            </div> :

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {familyGroups?.map((familyGroup) =>
            <FamilyGroupCard
              key={familyGroup?.id}
              familyGroup={familyGroup}
              onEdit={handleEditFamilyGroup}
              onDelete={handleDeleteFamilyGroup}
              onViewMembers={handleViewFamilyMembers} />

            )}
              
              {familyGroups?.length === 0 &&
            <div className="col-span-full text-center py-12">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No family groups yet</h3>
                  <p className="text-muted-foreground mb-6">
                    Create family groups to organize related guests and manage shared dietary preferences.
                  </p>
                  <Button
                variant="default"
                onClick={() => showSuccess('Family group creation feature coming soon')}
                iconName="Users"
                iconPosition="left">

                    Create Family Group
                  </Button>
                </div>
            }
            </div>
          }
        </div>
      </main>
      {/* Floating Action Button */}
      <FloatingActionButton onAction={handleFloatingAction} />
      {/* Modals */}
      <AddGuestModal
        isOpen={isAddGuestModalOpen}
        onClose={() => {
          setIsAddGuestModalOpen(false);
          setEditingGuest(null);
        }}
        onSave={handleSaveGuest}
        existingGuest={editingGuest} />

      <ImportContactsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportContacts} />

      <GuestHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        guest={selectedGuest} />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>);

};

export default GuestManagement;